// ──────────────────────────────────────────────────────────────────────────
// CommandPalette — a real Cmd+K palette.
//
//   <CommandPalette
//     open={open}
//     onClose={() => setOpen(false)}
//     commands={commands}    // see shape below
//   />
//
// Each command is:
//   {
//     id: 'unique-id',
//     label: 'Go to Orders',
//     hint?: 'Tab • Sales',           // optional secondary text
//     section: 'Navigation',          // grouping header
//     icon: <Package size={16} />,    // any react node
//     keywords?: 'orders sales',      // boost search matching
//     shortcut?: 'O',                 // optional badge
//     onRun: () => ...,
//   }
//
// Keyboard:
//   ↑ / ↓     navigate
//   Enter     run selected
//   Esc       close
//   Tab       cycle (with shift to reverse)
// ──────────────────────────────────────────────────────────────────────────

import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Search as SearchIcon, CornerDownLeft, ArrowUp, ArrowDown, Command as CmdIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

const popIn = keyframes`
  from { opacity: 0; transform: translateY(-8px) scale(0.985); }
  to   { opacity: 1; transform: translateY(0) scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: rgba(15, 23, 42, 0.45);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 12vh;
  animation: ${fadeIn} 160ms ease-out;

  @media (max-width: 640px) {
    padding-top: 4vh;
    align-items: flex-start;
  }
`;

const Panel = styled.div`
  width: min(640px, calc(100vw - 2rem));
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow:
    0 30px 60px -10px rgba(15, 23, 42, 0.35),
    0 18px 36px -18px rgba(15, 23, 42, 0.25);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: ${popIn} 180ms cubic-bezier(0.4, 0, 0.2, 1);
  max-height: min(70vh, 560px);
`;

const InputRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  padding: 0.95rem 1.1rem;
  border-bottom: 1px solid #f1f5f9;
  color: #94a3b8;

  input {
    flex: 1;
    border: none;
    background: transparent;
    outline: none;
    font-size: 1rem;
    color: #0f172a;
    font-family: inherit;

    &::placeholder { color: #94a3b8; }
  }
`;

const KbdHint = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.7rem;
  color: #94a3b8;

  kbd {
    display: inline-flex;
    align-items: center;
    gap: 0.2rem;
    padding: 0.12rem 0.4rem;
    background: #f1f5f9;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    color: #475569;
    font-size: 0.7rem;
    font-family: inherit;
  }
`;

const ResultList = styled.div`
  overflow-y: auto;
  padding: 0.4rem;
  flex: 1;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 999px; }
  &::-webkit-scrollbar-track { background: transparent; }
`;

const SectionHeader = styled.div`
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #94a3b8;
  padding: 0.6rem 0.7rem 0.3rem;
`;

const Item = styled.button`
  display: flex;
  align-items: center;
  gap: 0.7rem;
  width: 100%;
  padding: 0.6rem 0.7rem;
  background: ${p => p.$active ? 'linear-gradient(135deg, #eef2ff, #e0e7ff)' : 'transparent'};
  color: ${p => p.$active ? '#312e81' : '#0f172a'};
  border: none;
  border-radius: 9px;
  cursor: pointer;
  text-align: left;
  font-size: 0.875rem;
  font-family: inherit;
  transition: background 0.12s;
  outline: none;

  &:hover { background: ${p => p.$active ? 'linear-gradient(135deg, #eef2ff, #e0e7ff)' : '#f8fafc'}; }
`;

const ItemIcon = styled.span`
  width: 28px;
  height: 28px;
  border-radius: 7px;
  background: ${p => p.$active ? 'rgba(255,255,255,0.7)' : '#f1f5f9'};
  color: ${p => p.$active ? '#4338ca' : '#475569'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ItemBody = styled.span`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`;

const ItemLabel = styled.span`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ItemHint = styled.span`
  font-size: 0.72rem;
  color: ${p => p.$active ? '#6366f1' : '#94a3b8'};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Shortcut = styled.span`
  font-size: 0.68rem;
  color: ${p => p.$active ? '#4338ca' : '#94a3b8'};
  background: ${p => p.$active ? 'rgba(255,255,255,0.7)' : '#f1f5f9'};
  padding: 0.18rem 0.4rem;
  border-radius: 5px;
  border: 1px solid ${p => p.$active ? 'rgba(255,255,255,0.7)' : '#e2e8f0'};
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.55rem 0.9rem;
  border-top: 1px solid #f1f5f9;
  background: #fafbfd;
  color: #94a3b8;
  font-size: 0.72rem;

  @media (max-width: 640px) { display: none; }
`;

const FooterHints = styled.div`
  display: flex;
  align-items: center;
  gap: 0.85rem;

  kbd {
    display: inline-flex;
    align-items: center;
    padding: 0.12rem 0.4rem;
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 5px;
    color: #475569;
    font-size: 0.7rem;
    font-family: inherit;
    margin-right: 0.3rem;
  }
`;

const Empty = styled.div`
  padding: 2.25rem 1rem;
  text-align: center;
  color: #94a3b8;
  font-size: 0.875rem;

  div + div { margin-top: 0.4rem; font-size: 0.75rem; color: #cbd5e1; }
`;

/** Tiny fuzzy scoring — favours start-of-word and contiguous matches. */
const score = (haystack, needle) => {
  if (!needle) return 1;
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  if (h === n) return 1000;
  if (h.startsWith(n)) return 500 + (200 - Math.min(200, h.length));
  if (h.includes(n)) return 200 + (200 - Math.min(200, h.length));
  // letter-by-letter fuzzy
  let i = 0, s = 0;
  for (const c of h) {
    if (c === n[i]) { s += 5; i++; if (i === n.length) break; }
  }
  return i === n.length ? s : 0;
};

export const CommandPalette = ({ open, onClose, commands = [] }) => {
  const { t } = useTranslation();
  const [q, setQ] = useState('');
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    if (open) {
      setQ('');
      setActive(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Filter + score commands by query.
  const ranked = useMemo(() => {
    const list = commands.map((c) => {
      const haystack = `${c.label} ${c.section || ''} ${c.keywords || ''}`;
      return { cmd: c, s: score(haystack, q) };
    }).filter(r => r.s > 0);
    list.sort((a, b) => b.s - a.s);
    return list.map(r => r.cmd);
  }, [commands, q]);

  // Reset active when results change.
  useEffect(() => { setActive(0); }, [q]);

  // Group preserving rank order — first occurrence of each section sets the order.
  const grouped = useMemo(() => {
    const out = [];
    const seen = new Map();
    ranked.forEach((cmd) => {
      const sec = cmd.section || 'Other';
      if (!seen.has(sec)) {
        seen.set(sec, { section: sec, items: [] });
        out.push(seen.get(sec));
      }
      seen.get(sec).items.push(cmd);
    });
    return out;
  }, [ranked]);

  // Flat list for keyboard navigation index ↔ command lookup.
  const flat = useMemo(() => grouped.flatMap(g => g.items), [grouped]);

  // Scroll active item into view as the user navigates.
  useEffect(() => {
    const el = itemRefs.current[active];
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  if (!open) return null;

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { e.preventDefault(); onClose?.(); return; }
    if (e.key === 'ArrowDown' || (e.key === 'Tab' && !e.shiftKey)) {
      e.preventDefault();
      setActive((i) => (i + 1) % Math.max(1, flat.length));
    } else if (e.key === 'ArrowUp' || (e.key === 'Tab' && e.shiftKey)) {
      e.preventDefault();
      setActive((i) => (i - 1 + Math.max(1, flat.length)) % Math.max(1, flat.length));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const cmd = flat[active];
      if (cmd) {
        cmd.onRun?.();
        onClose?.();
      }
    }
  };

  let runningIdx = -1;

  return (
    <Backdrop onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <Panel onMouseDown={(e) => e.stopPropagation()}>
        <InputRow>
          <SearchIcon size={18} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t('palette.typeACommandOrJump')}
            spellCheck="false"
            autoComplete="off"
          />
          <KbdHint><kbd>esc</kbd> {t('palette.toClose')}</KbdHint>
        </InputRow>

        <ResultList>
          {flat.length === 0 ? (
            <Empty>
              <div>{t('palette.noMatchesFor')} <strong>"{q}"</strong></div>
              <div>{t('palette.trySearchingForOrdersDark')}</div>
            </Empty>
          ) : grouped.map((g) => (
            <div key={g.section}>
              <SectionHeader>{g.section}</SectionHeader>
              {g.items.map((cmd) => {
                runningIdx++;
                const isActive = runningIdx === active;
                const myIdx = runningIdx;
                return (
                  <Item
                    key={cmd.id}
                    ref={(el) => { if (el) itemRefs.current[myIdx] = el; }}
                    $active={isActive}
                    onMouseEnter={() => setActive(myIdx)}
                    onClick={() => { cmd.onRun?.(); onClose?.(); }}
                  >
                    <ItemIcon $active={isActive}>{cmd.icon}</ItemIcon>
                    <ItemBody>
                      <ItemLabel>{cmd.label}</ItemLabel>
                      {cmd.hint && <ItemHint $active={isActive}>{cmd.hint}</ItemHint>}
                    </ItemBody>
                    {cmd.shortcut && <Shortcut $active={isActive}>{cmd.shortcut}</Shortcut>}
                    {isActive && <CornerDownLeft size={14} style={{ color: '#6366f1' }} />}
                  </Item>
                );
              })}
            </div>
          ))}
        </ResultList>

        <Footer>
          <FooterHints>
            <span><kbd><ArrowUp size={10} /></kbd><kbd><ArrowDown size={10} /></kbd> navigate</span>
            <span><kbd><CornerDownLeft size={10} /></kbd> open</span>
            <span><kbd>tab</kbd> next</span>
          </FooterHints>
          <FooterHints>
            <CmdIcon size={12} /> <span>{t('palette.commandPalette')}</span>
          </FooterHints>
        </Footer>
      </Panel>
    </Backdrop>
  );
};

export default CommandPalette;
